import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import FilterMenu from "../../../components/FilterMenu";
import { DynamicDatePicker } from "../../../components/DatePicker";
import {
  getJvvnlCircleForecast,
  getJvvnlCircleDemandMetrics,
  getJvvnlDiscomSubDemandMetrics,
  getJvvnlDiscomSubstationForecast,
  getJvvnlFeederForecast,
  getJvvnlFeederMetrics,
  getJvvnlGeneration,
  getJvvnlGenerationForecast,
  getJvvnlWeatherByLocation,
  getJvvnlPlants,
  getJvvnlTranscoDemandMetrics,
  getJvvnlTranscoForecast,
  JvvnlEntityMetricsResponse,
} from "../../../Rest_api/RestApi";
import { formatDateForAPI } from "../../Rajasthan/Utility/const";
import { getJvvnlForecastLast7Days } from "../../../config/DefaultDates";
import { FeederChartPopup } from "../Demand/feederChart";
import { EmptyState } from "../../../components/recharts/ReMixCharts/components/EmptyState";
import { MetricCard, DataSummaryCard, WeatherCard } from "./MetricCard";
import type { JvvnlPlant } from "../../../models/jvvnl";

type EmbeddedNet = {
  level: "transco" | "discom_sub" | "plant" | "circle" | "feeder";
  id: number | string;
  label: string;
};

const VARIABLE_ITEMS = ["Demand", "Generation", "Net Load"];

const WEATHER_ITEMS = [
  { value: "cloud_cover", label: "Cloud Cover (%)" },
  {
    value: "direct_radiation_instant",
    label: "Direct Radiation Instant (W/m²)",
  },
  { value: "relative_humidity_2m", label: "Humidity (%)" },
  { value: "precipitation", label: "Precipitation (mm)" },
  { value: "rain", label: "Rain (mm)" },
  { value: "sunshine_duration", label: "Sunshine Duration (s)" },
  { value: "temperature_2m", label: "Temperature (°C)" },
  { value: "wind_speed_10m", label: "Wind 10m (km/h)" },
];

export function NetConsumptionPopup({
  embedded,
  onClose,
  initialTab,
  forecastDateOverride,
}: {
  embedded: EmbeddedNet;
  onClose?: () => void;
  initialTab?: "Actual" | "Forecast";
  forecastDateOverride?: string | null;
}) {
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const reduxRange =
    DefaultDates?.Metric?.defaultRangeDate ??
    DefaultDates?.Overview?.defaultRangeDate ??
    DefaultDates?.Actual?.defaultRangeDate;

  const [localRange, setLocalRange] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  } | null>(() => {
    if (initialTab === "Forecast" && forecastDateOverride) {
      // Forecast tab pins to the model's forecast_date (the label date), single day.
      const d = new Date(forecastDateOverride);
      return { startDate: d, endDate: d, key: "selection" };
    }
    if (!reduxRange?.startDate || !reduxRange?.endDate) return null;
    if (initialTab === "Actual") {
      const end = new Date(reduxRange.endDate);
      return { startDate: end, endDate: end, key: "selection" };
    }
    // Forecast tab: default to today-ending 7-day range (forecast available today).
    // Actual tab fallback: use redux range (T-1 ending).
    if (initialTab === "Forecast") {
      return getJvvnlForecastLast7Days();
    }
    return {
      startDate: new Date(reduxRange.startDate),
      endDate: new Date(reduxRange.endDate),
      key: "selection",
    };
  });

  useEffect(() => {
    if (initialTab === "Forecast" && forecastDateOverride) {
      const d = new Date(forecastDateOverride);
      setLocalRange((prev) => {
        // Feeder keeps its picker → don't clobber a user-selected multi-day range.
        if (
          embedded.level === "feeder" &&
          prev?.startDate &&
          prev?.endDate &&
          formatDateForAPI(prev.startDate) !== formatDateForAPI(prev.endDate)
        ) {
          return prev;
        }
        const fmtPrev = prev?.startDate
          ? formatDateForAPI(prev.startDate)
          : null;
        if (fmtPrev === forecastDateOverride) return prev;
        return { startDate: d, endDate: d, key: "selection" };
      });
    }
  }, [initialTab, forecastDateOverride, embedded.level]);

  const range = localRange ?? reduxRange;

  const dateParams = useMemo(() => {
    if (!range?.startDate || !range?.endDate) return null;
    return {
      startDate: formatDateForAPI(range.startDate),
      endDate: formatDateForAPI(range.endDate),
    };
  }, [range?.startDate, range?.endDate]);

  // Feeder Actual Load card follows the DISPLAYED range (the picker/header day),
  // so the card, chart, and header all agree. End is clamped to today so a
  // future-dated picker (forecast tab) doesn't query actuals that can't exist.
  const actualDateParams = useMemo(() => {
    if (!range?.startDate || !range?.endDate) return null;
    const today = new Date();
    const startD = new Date(range.startDate);
    const endD = new Date(range.endDate);
    // Clamp both ends to today — no actuals exist beyond today.
    const safeStart = startD > today ? today : startD;
    const safeEnd = endD > today ? today : endD;
    return {
      startDate: formatDateForAPI(safeStart),
      endDate: formatDateForAPI(safeEnd),
    };
  }, [range?.startDate, range?.endDate]);

  const [selectedVariables, setSelectedVariables] = useState<string[]>(() => {
    if (embedded.level === "plant") return ["Generation"];
    if (embedded.level === "feeder")
      return initialTab === "Actual" ? ["Actual", "Forecast"] : ["Forecast"];
    return ["Demand", "Generation", "Net Load"];
  });
  const [plantMetrics, setPlantMetrics] = useState<{
    peakGenKw: number | null;
    avgGenKw: number | null;
    peakTemp: number | null;
    avgTemp: number | null;
    avgHumid: number | null;
    avgSolar: number | null;
  } | null>(null);
  const [selectedWeather, setSelectedWeather] = useState<string>("");
  // Feeder picked inside the embedded chart (old SearchBox or new
  // HierarchicalSelectModal). Overrides embedded.id for the metric cards so
  // they track the in-chart selection. null = use embedded.id.
  const [activeFeeder, setActiveFeeder] = useState<{
    id: number;
    name: string;
  } | null>(null);
  // Full feeder selection (drives the multi-select chart heading).
  const [activeFeederList, setActiveFeederList] = useState<
    { id: number; name: string }[]
  >([]);
  // Net-mode in-chart selection (circle/transco/discom_sub). Overrides
  // embedded.level/id/label for metric cards + heading so they track the
  // Circle→Transco→Discom selector inside the chart. null = use embedded.
  const [activeNet, setActiveNet] = useState<{
    level: "discom_sub" | "transco" | "circle";
    id: number | string | undefined;
    name: string;
  } | null>(null);
  // Reset when the popup target entity changes.
  useEffect(() => {
    setActiveFeeder(null);
    setActiveFeederList([]);
    setActiveNet(null);
  }, [embedded.id, embedded.level]);

  // Effective entity for metric fetches + heading: in-chart pick wins.
  const effLevel = activeNet ? activeNet.level : embedded.level;
  const effId = activeNet && activeNet.id != null ? activeNet.id : embedded.id;
  const effLabel = activeNet ? activeNet.name : embedded.label;
  // Effective feeder id for metric fetches: in-chart pick wins over the prop.
  const metricFeederId =
    embedded.level === "feeder" && activeFeeder ? activeFeeder.id : effId;
  const [metrics, setMetrics] = useState<JvvnlEntityMetricsResponse | null>(
    null,
  );
  // Feeder shows Actual Load + Forecasted Load + Data Summary on BOTH tabs,
  // so fetch both sources independently of initialTab.
  const [feederActual, setFeederActual] =
    useState<JvvnlEntityMetricsResponse | null>(null);
  const [feederForecast, setFeederForecast] =
    useState<JvvnlEntityMetricsResponse | null>(null);
  // True when the Forecast-tab fetch resolved with no usable data for the
  // selected date → render a no-data screen instead of cards + chart.
  const [forecastEmpty, setForecastEmpty] = useState(false);

  useEffect(() => {
    if (embedded.level === "plant") {
      setSelectedVariables(["Generation"]);
      return;
    }
    if (embedded.level === "feeder") {
      setSelectedVariables(
        initialTab === "Actual" ? ["Actual", "Forecast"] : ["Forecast"],
      );
      return;
    }
    setSelectedVariables(["Demand", "Generation", "Net Load"]);
  }, [embedded.level, initialTab]);

  useEffect(() => {
    if (!dateParams) return;
    setMetrics(null);
    setForecastEmpty(false);
    if (effLevel === "plant") {
      return;
    }
    let cancelled = false;
    const level: "transco" | "discom_sub" | "circle" | "feeder" = effLevel;

    // Forecast envelope (metrics.full) → internal kW-based metrics shape.
    // Store everything as kW; the display layer divides /1000 for circle/transco.
    const forecastToMetrics = (
      full: any,
    ): JvvnlEntityMetricsResponse | null => {
      if (!full) return null;
      // The forecast wrapper runs mapDemandResult, which normalizes the API's
      // *_kw fields into peak_mw/avg_mw for every level. The VALUE is true MW
      // at circle/transco but raw kW at discom_sub/feeder (just renamed), so
      // only scale the MW levels up to kW.
      const isMw = level === "circle" || level === "transco";
      const peak = full.peak_mw;
      const avg = full.avg_mw;
      if (peak == null) return null;
      const scale = isMw ? 1000 : 1;
      return {
        peak_load_kw: Number(peak) * scale,
        avg_load_kw: avg != null ? Number(avg) * scale : undefined,
        // Generation / net read by cards as-is (MW at circle/transco, kW at
        // discom_sub — matching displayed unit).
        peak_generation_mw: full.peak_generation_mw ?? null,
        avg_generation_mw: full.avg_generation_mw ?? null,
        peak_net_mw: full.peak_net_mw ?? null,
        avg_net_mw: full.avg_net_mw ?? null,
        peak_temp_c:
          full.peak_weather?.temp != null
            ? Number(full.peak_weather.temp)
            : undefined,
        avg_temp_c:
          full.avg_weather?.temp != null
            ? Number(full.avg_weather.temp)
            : undefined,
      };
    };

    // Tab decides the source — no fallback.
    if (initialTab === "Forecast") {
      const forecastApi = {
        feeder: getJvvnlFeederForecast,
        circle: getJvvnlCircleForecast,
        transco: getJvvnlTranscoForecast,
        discom_sub: getJvvnlDiscomSubstationForecast,
      }[level];
      // Every level (circle included) filters by id so the popup metrics match
      // the page cards, which always pass ids=<entityId> at the same scope.
      forecastApi({
        ids: [Number(metricFeederId)],
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
      })
        .then((res: any) => {
          if (cancelled) return;
          const mapped = forecastToMetrics(res.results?.[0]?.metrics?.full);
          setMetrics(mapped);
          setForecastEmpty(mapped == null);
        })
        .catch(() => {
          if (!cancelled) {
            setMetrics(null);
            setForecastEmpty(true);
          }
        });
    } else if (level === "feeder") {
      // Feeder Actual cards read the dedicated `feederActual`/`feederForecast`
      // state (fetched in the effect below), not `metrics` — so no fetch here.
      // The Forecast tab is handled by the `initialTab === "Forecast"` branch
      // above, which drives `forecastEmpty`.
    } else {
      // Use the group demand-metrics endpoints (same as the inline page) so the
      // Generation + Net Load cards get populated — entity-metrics only returns
      // load. full.* values: circle/transco = MW, discom_sub = raw kW.
      const isMw = level === "circle" || level === "transco";
      const groupApi = {
        circle: getJvvnlCircleDemandMetrics,
        transco: getJvvnlTranscoDemandMetrics,
        discom_sub: getJvvnlDiscomSubDemandMetrics,
      }[level];
      // group demand-metrics filter by numeric id at EVERY level (circle
      // included) so the popup matches the page cards, which always scope to
      // ids=<entityId>.
      const idArg = { ids: [Number(effId)] };
      const demandToMetrics = (
        full: any,
      ): JvvnlEntityMetricsResponse | null => {
        if (full?.peak_mw == null) return null;
        const scale = isMw ? 1000 : 1;
        return {
          // Store load in kW so the display layer can divide /1000 for MW levels.
          peak_load_kw: Number(full.peak_mw) * scale,
          avg_load_kw:
            full.avg_mw != null ? Number(full.avg_mw) * scale : undefined,
          // Generation / net are read by the cards as-is (MW at circle/transco,
          // kW at discom_sub — matching the card's displayed unit).
          peak_generation_mw: full.peak_generation_mw ?? null,
          avg_generation_mw: full.avg_generation_mw ?? null,
          peak_net_mw: full.peak_net_mw ?? null,
          avg_net_mw: full.avg_net_mw ?? null,
        };
      };
      groupApi({
        ...idArg,
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        level,
      })
        .then((res: any) => {
          if (!cancelled)
            setMetrics(demandToMetrics(res.results?.[0]?.metrics?.full));
        })
        .catch(() => {
          if (!cancelled) setMetrics(null);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [dateParams, effId, effLevel, initialTab, metricFeederId]);

  // Feeder: fetch BOTH actual + forecast so the cards (Actual Load, Forecasted
  // Load, Data Summary) render on both tabs regardless of initialTab.
  useEffect(() => {
    if (!dateParams || !actualDateParams) return;
    if (embedded.level !== "feeder") {
      setFeederActual(null);
      setFeederForecast(null);
      return;
    }
    let cancelled = false;
    setFeederActual(null);
    setFeederForecast(null);

    // Actual (raw kW value, renamed peak_mw/avg_mw by the mapper) + quality.
    const actualToMetrics = (full: any): JvvnlEntityMetricsResponse | null => {
      if (full?.peak_mw == null) return null;
      return {
        peak_load_kw: Number(full.peak_mw),
        avg_load_kw: full.avg_mw != null ? Number(full.avg_mw) : undefined,
        available_pct: full.available_pct ?? null,
        zeros_pct: full.zeros_pct ?? null,
        nulls_pct: full.nulls_pct ?? null,
      };
    };
    const fcstToMetrics = (full: any): JvvnlEntityMetricsResponse | null => {
      if (full?.peak_mw == null) return null;
      return {
        peak_load_kw: Number(full.peak_mw),
        avg_load_kw: full.avg_mw != null ? Number(full.avg_mw) : undefined,
      };
    };

    getJvvnlFeederMetrics({
      startDate: actualDateParams.startDate,
      endDate: actualDateParams.endDate,
      level: "feeder",
      ids: [metricFeederId],
      combine: true,
    })
      .then((res) => {
        if (!cancelled)
          setFeederActual(actualToMetrics(res.results?.[0]?.metrics?.full));
      })
      .catch(() => {
        if (!cancelled) setFeederActual(null);
      });

    getJvvnlFeederForecast({
      ids: [Number(metricFeederId)],
      startDate: dateParams.startDate,
      endDate: dateParams.endDate,
    })
      .then((res: any) => {
        if (!cancelled)
          setFeederForecast(fcstToMetrics(res.results?.[0]?.metrics?.full));
      })
      .catch(() => {
        if (!cancelled) setFeederForecast(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    dateParams,
    actualDateParams,
    embedded.id,
    embedded.level,
    metricFeederId,
  ]);

  useEffect(() => {
    if (!dateParams) return;
    if (embedded.level !== "plant") {
      setPlantMetrics(null);
      return;
    }

    let cancelled = false;
    const primaryPlantId = Number(embedded.id);
    if (!Number.isFinite(primaryPlantId)) {
      setPlantMetrics(null);
      return;
    }
    const plantIds = [primaryPlantId];
    const plantIdSet = new Set(plantIds);

    (async () => {
      try {
        const plants = ((await getJvvnlPlants()) || []) as JvvnlPlant[];
        // Primary plant drives weather/location; gen metrics aggregate across all.
        const plant = plants.find(
          (p) => (p.id ?? p.plant_id) === primaryPlantId,
        );
        if (!plant) {
          if (!cancelled) setPlantMetrics(null);
          return;
        }

        const plantLocation = new Map<number, number>();
        for (const p of plants) {
          const pid = p.id ?? p.plant_id;
          const loc = p.location ?? p.location_id;
          if (pid != null && loc != null) plantLocation.set(pid, loc);
        }

        // Weather always comes from the actual-generation endpoint
        // (the forecast endpoint returns gen only, no weather rows).
        const { rows, weatherByLocation } = await getJvvnlGeneration({
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
          plantIds,
          plantLocation,
        });

        if (cancelled) return;

        // On the Forecast tab pull gen from the forecast endpoint; the actual
        // table has no rows for a future forecast date, leaving cards blank.
        // Forecast endpoint is single-plant, so fetch + merge per selected plant.
        let genRows: { plant_id: number | null; generation_kw: number }[] =
          rows;
        if (initialTab === "Forecast") {
          const fcstResults = await Promise.all(
            plantIds.map((id) =>
              getJvvnlGenerationForecast({
                level: "plant",
                ids: id,
                start_date: dateParams.startDate,
                end_date: dateParams.endDate,
              }).then((f) => f.rows),
            ),
          );
          if (cancelled) return;
          genRows = fcstResults.flat();
        }

        const genValues = genRows
          .filter((r) => r.plant_id != null && plantIdSet.has(r.plant_id))
          .map((r) => r.generation_kw)
          .filter((v): v is number => typeof v === "number" && isFinite(v));
        const peakGenKw = genValues.length ? Math.max(...genValues) : null;
        const avgGenKw = genValues.length
          ? genValues.reduce((sum, v) => sum + v, 0) / genValues.length
          : null;

        // weatherByLocation is keyed off actual gen rows, which are empty on a
        // future forecast date → no weather. On Forecast tab fetch the plant's
        // location weather directly (the /weather/ endpoint serves forecast).
        const locId = plant.location ?? plant.location_id;
        let weatherRows =
          locId != null ? (weatherByLocation.get(locId) ?? []) : [];
        if (initialTab === "Forecast" && locId != null && !weatherRows.length) {
          weatherRows = await getJvvnlWeatherByLocation({
            startDate: dateParams.startDate,
            endDate: dateParams.endDate,
            locationId: locId,
          });
          if (cancelled) return;
        }
        const avgOf = (vals: number[]) =>
          vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
        const finite = (v: unknown): v is number =>
          typeof v === "number" && isFinite(v);
        const tempValues = weatherRows
          .map((w) => w.temperature_2m)
          .filter(finite);
        const humidValues = weatherRows
          .map((w) => w.relative_humidity_2m)
          .filter(finite);
        const solarValues = weatherRows
          .map((w) => w.direct_radiation_instant)
          .filter(finite);
        const peakTemp = tempValues.length ? Math.max(...tempValues) : null;
        const avgTemp = avgOf(tempValues);
        const avgHumid = avgOf(humidValues);
        const avgSolar = avgOf(solarValues);

        setPlantMetrics({
          peakGenKw,
          avgGenKw,
          peakTemp,
          avgTemp,
          avgHumid,
          avgSolar,
        });
      } catch {
        if (!cancelled) setPlantMetrics(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dateParams, embedded.id, embedded.level, initialTab]);

  // feeder + discom_sub + plant: keep kW; transco/circle: convert kW→MW for display.
  const showAsKw =
    effLevel === "feeder" || effLevel === "discom_sub" || effLevel === "plant";
  const loadUnit = showAsKw ? "kW" : "MW";
  const peakLoadDisplay =
    effLevel === "plant"
      ? (plantMetrics?.peakGenKw ?? null)
      : metrics?.peak_load_kw != null
        ? showAsKw
          ? metrics.peak_load_kw
          : metrics.peak_load_kw / 1000
        : null;
  const avgLoadDisplay =
    effLevel === "plant"
      ? (plantMetrics?.avgGenKw ?? null)
      : metrics?.avg_load_kw != null
        ? showAsKw
          ? metrics.avg_load_kw
          : metrics.avg_load_kw / 1000
        : null;

  // Feeder cards (kW, shown on both tabs).
  const feederActualPeak = feederActual?.peak_load_kw ?? null;
  const feederActualAvg = feederActual?.avg_load_kw ?? null;
  const feederFcstPeak = feederForecast?.peak_load_kw ?? null;
  const feederFcstAvg = feederForecast?.avg_load_kw ?? null;

  // circle / transco / discom_sub card titles vary by tab.
  const titlePrefix = initialTab === "Forecast" ? "Forecasted" : "Actual";

  // Heading: when multiple feeders are picked in-chart, show the first name + a
  // "+N" pill, with all names in the tooltip; otherwise the single label.
  const isMultiFeeder =
    embedded.level === "feeder" && activeFeederList.length > 1;
  const headingLabel = isMultiFeeder
    ? activeFeederList[0].name
    : embedded.level === "feeder" && activeFeeder
      ? activeFeeder.name
      : embedded.level === "feeder" && activeFeederList.length === 1
        ? activeFeederList[0].name
        : effLabel;
  const allFeederNames = activeFeederList.map((f) => f.name).join(", ");

  return (
    <div className="bg-[#F4F6F9] rounded-2xl flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-white rounded-t-2xl border-b border-[#E5EAF0] shrink-0">
        <span
          className="text-[18px] font-medium text-[#525062] inline-flex items-center"
          title={isMultiFeeder ? allFeederNames : undefined}
        >
          {headingLabel}
          {isMultiFeeder && (
            <span className="ml-1.5 inline-flex items-center rounded-full bg-[#FEF0E7] px-2 py-0.5 text-[12px] font-semibold text-[#F1935C]">
              +{activeFeederList.length - 1}
            </span>
          )}
          <span className="text-[#8C8A96] font-normal ml-1">
            (
            {effLevel === "transco"
              ? "Transco"
              : effLevel === "discom_sub"
                ? "Discom Substation"
                : effLevel === "circle"
                  ? "Circle"
                  : effLevel === "feeder"
                    ? "Feeder"
                    : "Plant"}
            )
          </span>
          {initialTab === "Forecast" && (
            <span className="text-[14px] font-normal text-[#8C8A96] ml-2">
              - Forecasted
            </span>
          )}
        </span>
        <div className="flex items-center gap-3">
          {localRange &&
            !(initialTab === "Forecast" && embedded.level !== "feeder") && (
              <DynamicDatePicker
                setDate={(dates) => {
                  if (dates[0]) setLocalRange(dates[0]);
                }}
                defaultDates={localRange}
              />
            )}
          {onClose && (
            <button
              onClick={onClose}
              className="text-[#6B7280] text-lg leading-none px-2"
              aria-label="Close"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Body: sidebar + content */}
      <div className="flex gap-2 p-2 flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-[180px] shrink-0 bg-white rounded-xl border border-[#E5EAF0] p-3 overflow-y-auto">
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Variable"
              items={
                embedded.level === "plant"
                  ? ["Generation"]
                  : embedded.level === "feeder"
                    ? ["Actual", "Forecast", "Weather"]
                    : VARIABLE_ITEMS
              }
              selectedItems={selectedVariables}
              setSelectedItems={(next) => {
                const hadWeather = selectedVariables.includes("Weather");
                const hasWeather = next.includes("Weather");
                // Selecting "Weather" with nothing picked yet auto-selects
                // Cloud Cover; deselecting it clears the weather param.
                if (hasWeather && !hadWeather && !selectedWeather) {
                  setSelectedWeather("cloud_cover");
                } else if (!hasWeather && hadWeather) {
                  setSelectedWeather("");
                }
                setSelectedVariables(next);
              }}
              sortAlphabetically={false}
            />
            <FilterMenu
              title="Weather"
              items={WEATHER_ITEMS}
              showEmptySelected={false}
              selectedItems={selectedWeather ? [selectedWeather] : []}
              onSelect={(value) => {
                const newWeather = selectedWeather === value ? "" : value;
                setSelectedWeather(newWeather);
                if (embedded.level === "feeder") {
                  setSelectedVariables((prev) => {
                    const base = prev.filter((v) => v !== "Weather");
                    return newWeather ? [...base, "Weather"] : base;
                  });
                }
              }}
              sortAlphabetically={false}
            />
          </div>
        </div>

        {/* Right column */}
        <div className="flex-1 min-w-0 flex flex-col gap-2 min-h-0">
          {forecastEmpty ? (
            <div className="relative flex-1 bg-white rounded-xl border border-[#E5EAF0]">
              <EmptyState />
            </div>
          ) : (
            <>
              {/* Metric cards */}
              {embedded.level === "feeder" ? (
                <div className="grid grid-cols-3 gap-2">
                  <MetricCard
                    compact
                    title="Actual Load"
                    subtitle={
                      actualDateParams
                        ? `(Upto ${actualDateParams.endDate
                            .split("-")
                            .reverse()
                            .join("/")})`
                        : undefined
                    }
                    averageLabel="Average"
                    peakValue={feederActualPeak}
                    averageValue={feederActualAvg}
                    unit={loadUnit}
                    accentColor="#E25A1F"
                    showWeather={false}
                    theme={{
                      barBackground:
                        "linear-gradient(180deg, #ED9D2B 0%, #D8800F 100%)",
                      iconBackground:
                        "linear-gradient(150deg, #ED9D2B 0%, #D8800F 100%)",
                      iconColor: "white",
                      titleColor: "#A8540B",
                      peakLabelColor: "#A8540B80",
                      peakTextColor: "#A8540B",
                      peakDateBg: "#A8540B15",
                      peakDateColor: "#A8540B",
                      peakSlotBg: "#8C8A96",
                    }}
                  />
                  <MetricCard
                    compact
                    title="Forecasted Load"
                    averageLabel="Average"
                    peakValue={feederFcstPeak}
                    averageValue={feederFcstAvg}
                    unit={loadUnit}
                    accentColor="#16A34A"
                    showWeather={false}
                    theme={{
                      barBackground:
                        "linear-gradient(180deg, #22C55E 0%, #16A34A 100%)",
                      iconBackground:
                        "linear-gradient(150deg, #22C55E 0%, #16A34A 100%)",
                      iconColor: "white",
                      titleColor: "#15803D",
                      peakLabelColor: "#15803D80",
                      peakTextColor: "#15803D",
                      peakDateBg: "#15803D15",
                      peakDateColor: "#15803D",
                      peakSlotBg: "#8C8A96",
                    }}
                  />
                  <DataSummaryCard
                    compact
                    availableValue={feederActual?.available_pct ?? null}
                    zerosValue={feederActual?.zeros_pct ?? null}
                    nullsValue={feederActual?.nulls_pct ?? null}
                    showBreakdown={false}
                  />
                </div>
              ) : embedded.level === "plant" ? (
                <div className="grid grid-cols-2 gap-3">
                  <MetricCard
                    compact
                    title="Generation"
                    peakValue={peakLoadDisplay}
                    averageValue={avgLoadDisplay}
                    unit={loadUnit}
                    accentColor="#10B981"
                    showWeather={false}
                  />
                  <WeatherCard
                    compact
                    temp={plantMetrics?.avgTemp ?? null}
                    humid={plantMetrics?.avgHumid ?? null}
                    solar={plantMetrics?.avgSolar ?? null}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <MetricCard
                    compact
                    title={`${titlePrefix} Load`}
                    peakValue={peakLoadDisplay}
                    averageValue={avgLoadDisplay}
                    unit={loadUnit}
                    accentColor="#E25A1F"
                    showWeather={false}
                    theme={{
                      barBackground:
                        "linear-gradient(180deg, #ED9D2B 0%, #D8800F 100%)",
                      iconBackground:
                        "linear-gradient(150deg, #ED9D2B 0%, #D8800F 100%)",
                      iconColor: "white",
                      titleColor: "#A8540B",
                      peakLabelColor: "#A8540B80",
                      peakTextColor: "#A8540B",
                      peakDateBg: "#A8540B15",
                      peakDateColor: "#A8540B",
                      peakSlotBg: "#8C8A96",
                    }}
                  />
                  <MetricCard
                    compact
                    title={`${titlePrefix} Generation`}
                    peakValue={metrics?.peak_generation_mw ?? null}
                    averageValue={metrics?.avg_generation_mw ?? null}
                    unit={loadUnit}
                    accentColor="#10B981"
                    showWeather={false}
                    emptyMessage={
                      peakLoadDisplay != null
                        ? "No generation reported for this entity"
                        : undefined
                    }
                    theme={{
                      barBackground:
                        "linear-gradient(180deg, #10B981 0%, #059669 100%)",
                      iconBackground:
                        "linear-gradient(150deg, #10B981 0%, #059669 100%)",
                      iconColor: "white",
                      titleColor: "#047857",
                      peakLabelColor: "#04785780",
                      peakTextColor: "#047857",
                      peakDateBg: "#04785715",
                      peakDateColor: "#047857",
                      peakSlotBg: "#8C8A96",
                    }}
                  />
                  <MetricCard
                    compact
                    title={`${titlePrefix} Net Load`}
                    peakValue={metrics?.peak_net_mw ?? null}
                    averageValue={metrics?.avg_net_mw ?? null}
                    unit={loadUnit}
                    accentColor="#8B5CF6"
                    showWeather={false}
                    theme={{
                      barBackground:
                        "linear-gradient(180deg, #8B5CF6 0%, #7C3AED 100%)",
                      iconBackground:
                        "linear-gradient(150deg, #8B5CF6 0%, #7C3AED 100%)",
                      iconColor: "white",
                      titleColor: "#7139D0",
                      peakLabelColor: "#7139D080",
                      peakTextColor: "#7139D0",
                      peakDateBg: "#6D28D915",
                      peakDateColor: "#6D28D9",
                      peakSlotBg: "#8C8A96",
                    }}
                  />
                </div>
              )}

              {/* Chart: reuse existing embedded flow */}
              <div className="bg-white rounded-xl border border-[#E5EAF0] [&_.chart-container]:!h-[calc(100vh-400px)] [&_.chartTable]:!h-[calc(100vh-360px)] [&_.gas-chart-container]:!min-h-0 [&_.gas-chart-container]:!flex-1 [&_.gas-chart-container>div]:!min-h-0 [&_.gas-chart-container>div]:!h-full flex-1 flex flex-col [&>div]:flex-1 [&>div]:flex [&>div]:flex-col [&>div]:min-h-0">
                <FeederChartPopup
                  tooltipBoundary="viewport"
                  emptyStateHeight="100%"
                  embedded={embedded}
                  onEmbeddedFeederChange={
                    embedded.level === "feeder" ? setActiveFeeder : undefined
                  }
                  onEmbeddedFeederListChange={
                    embedded.level === "feeder"
                      ? setActiveFeederList
                      : undefined
                  }
                  onEmbeddedNetChange={
                    embedded.level === "transco" ||
                    embedded.level === "discom_sub" ||
                    embedded.level === "circle"
                      ? setActiveNet
                      : undefined
                  }
                  weatherParam={selectedWeather}
                  netVariable={
                    embedded.level === "feeder"
                      ? null
                      : initialTab !== "Forecast" &&
                          selectedVariables.length === 1
                        ? (selectedVariables[0] as
                            | "Demand"
                            | "Generation"
                            | "Net Load")
                        : null
                  }
                  variablesOverride={
                    embedded.level === "feeder"
                      ? selectedWeather
                        ? [
                            ...selectedVariables.filter((v) => v !== "Weather"),
                            "Weather",
                          ]
                        : selectedVariables.filter((v) => v !== "Weather")
                      : initialTab === "Forecast"
                        ? selectedWeather
                          ? ["Forecast", "Weather"]
                          : ["Forecast"]
                        : selectedWeather
                          ? ["Actual", "Weather"]
                          : ["Actual"]
                  }
                  forecastVariants={
                    embedded.level !== "feeder" && initialTab === "Forecast"
                      ? selectedVariables
                      : null
                  }
                  actualVariants={
                    embedded.level !== "feeder" && initialTab !== "Forecast"
                      ? selectedVariables
                      : null
                  }
                  dateRangeOverride={localRange}
                  forecastDateOverride={
                    initialTab === "Forecast"
                      ? // Single-day override only when the picker is on one day.
                        // For a real range (start !== end), pass null so the child
                        // uses the start_date/end_date range path and plots every day.
                        localRange?.startDate && localRange?.endDate
                        ? formatDateForAPI(localRange.startDate) ===
                          formatDateForAPI(localRange.endDate)
                          ? formatDateForAPI(localRange.startDate)
                          : null
                        : forecastDateOverride
                      : null
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
